import { useState } from 'react';
import { User, Mail, Lock, Edit2, Check, X, Briefcase } from 'lucide-react';
import { useAuthStore } from '../../stores/auth-store';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { changePassword, updateCognitoName, requestEmailChange, confirmEmailChange } from '../../lib/auth';
import { api } from '../../lib/api-client';

const ROLE_LABELS: Record<string, string> = {
  gerente: 'Gerente General',
  operaciones: 'Jefe de Operaciones',
  administrativo: 'Administrativo / Jefe de Proyecto',
  residente: 'Residente de Obra',
  compras: 'Compras',
  contabilidad: 'Contabilidad',
};

export function ProfilePage() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  // Nombre editing
  const [editingNombre, setEditingNombre] = useState(false);
  const [nombreDraft, setNombreDraft] = useState('');
  const [savingNombre, setSavingNombre] = useState(false);
  const [nombreError, setNombreError] = useState('');

  // Cargo editing
  const [editingCargo, setEditingCargo] = useState(false);
  const [cargoDraft, setCargoDraft] = useState('');
  const [savingCargo, setSavingCargo] = useState(false);
  const [cargoError, setCargoError] = useState('');

  // Email editing
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailDraft, setEmailDraft] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailStep, setEmailStep] = useState<'edit' | 'verify'>('edit');
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [pendingNewEmail, setPendingNewEmail] = useState('');

  // Password changing
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  if (!user) return null;

  // — Nombre handlers —
  const startEditNombre = () => {
    setNombreDraft(user.nombre);
    setNombreError('');
    setEditingNombre(true);
  };

  const cancelEditNombre = () => {
    setEditingNombre(false);
    setNombreError('');
  };

  const saveNombre = async () => {
    const trimmed = nombreDraft.trim();
    if (trimmed.length < 2) {
      setNombreError('El nombre debe tener al menos 2 caracteres.');
      return;
    }
    if (trimmed === user.nombre) {
      setEditingNombre(false);
      return;
    }
    setSavingNombre(true);
    setNombreError('');
    try {
      await updateCognitoName(trimmed);
      await api.put('/api/me', { nombre: trimmed });
      setUser({ ...user, nombre: trimmed });
      setEditingNombre(false);
    } catch {
      setNombreError('No se pudo actualizar el nombre. Intente nuevamente.');
    } finally {
      setSavingNombre(false);
    }
  };

  // — Cargo handlers —
  const startEditCargo = () => {
    setCargoDraft(user.cargo ?? '');
    setCargoError('');
    setEditingCargo(true);
  };

  const cancelEditCargo = () => {
    setEditingCargo(false);
    setCargoError('');
  };

  const saveCargo = async () => {
    const trimmed = cargoDraft.trim();
    if (trimmed.length > 100) {
      setCargoError('El cargo no puede superar los 100 caracteres.');
      return;
    }
    if (trimmed === (user.cargo ?? '')) {
      setEditingCargo(false);
      return;
    }
    setSavingCargo(true);
    setCargoError('');
    try {
      await api.put('/api/me', { cargo: trimmed || null });
      setUser({ ...user, cargo: trimmed || null });
      setEditingCargo(false);
    } catch {
      setCargoError('No se pudo actualizar el cargo. Intente nuevamente.');
    } finally {
      setSavingCargo(false);
    }
  };

  // — Email handlers —
  const startEditEmail = () => {
    setEmailDraft(user.email);
    setEmailError('');
    setEmailStep('edit');
    setEditingEmail(true);
  };

  const cancelEditEmail = () => {
    setEditingEmail(false);
    setEmailStep('edit');
    setEmailError('');
    setVerifyCode('');
    setPendingNewEmail('');
  };

  const requestEmail = async () => {
    const trimmed = emailDraft.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError('Ingrese un correo electrónico válido.');
      return;
    }
    if (trimmed === user.email) {
      setEditingEmail(false);
      return;
    }
    setSavingEmail(true);
    setEmailError('');
    try {
      await requestEmailChange(trimmed);
      setPendingNewEmail(trimmed);
      setEmailStep('verify');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('AliasExistsException') || msg.includes('already exists')) {
        setEmailError('Este correo ya está en uso por otra cuenta.');
      } else {
        setEmailError('No se pudo enviar el código de verificación. Intente nuevamente.');
      }
    } finally {
      setSavingEmail(false);
    }
  };

  const confirmEmail = async () => {
    if (verifyCode.trim().length < 4) {
      setEmailError('Ingrese el código de verificación.');
      return;
    }
    setVerifyingEmail(true);
    setEmailError('');
    try {
      await confirmEmailChange(verifyCode.trim());
      await api.put('/api/me', { email: pendingNewEmail });
      setUser({ ...user, email: pendingNewEmail });
      cancelEditEmail();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('CodeMismatchException') || msg.includes('Invalid')) {
        setEmailError('El código de verificación es incorrecto.');
      } else if (msg.includes('ExpiredCodeException')) {
        setEmailError('El código expiró. Solicite uno nuevo.');
      } else {
        setEmailError('No se pudo confirmar el cambio de correo. Intente nuevamente.');
      }
    } finally {
      setVerifyingEmail(false);
    }
  };

  // — Password handler —
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    if (newPassword.length < 8) {
      setPasswordError('La nueva contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('La nueva contraseña y la confirmación no coinciden.');
      return;
    }

    setIsSubmitting(true);
    try {
      await changePassword(oldPassword, newPassword);
      setPasswordSuccess(true);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('NotAuthorizedException') || msg.includes('Incorrect')) {
        setPasswordError('La contraseña actual es incorrecta.');
      } else if (msg.includes('LimitExceededException')) {
        setPasswordError('Demasiados intentos. Intente más tarde.');
      } else {
        setPasswordError('No se pudo cambiar la contraseña. Intente nuevamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 fade-up max-w-lg">
      <h1 className="text-2xl font-bold text-brand-dark">Mi Perfil</h1>

      <Card>
        <div className="p-6 space-y-6">
          {/* Avatar */}
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#3c98ff] text-2xl font-bold text-white">
              {user.nombre.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-brand-dark">{user.nombre}</h2>
              <p className="text-sm text-gray-500">{ROLE_LABELS[user.role] ?? user.role}</p>
            </div>
          </div>

          <hr className="border-gray-200" />

          {/* Editable fields */}
          <div className="space-y-5">
            {/* Nombre */}
            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 mb-1">Nombre</p>
                {editingNombre ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={nombreDraft}
                        onChange={(e) => setNombreDraft(e.target.value)}
                        className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#3c98ff]"
                        disabled={savingNombre}
                        autoFocus
                        onKeyDown={(e) => { if (e.key === 'Enter') saveNombre(); if (e.key === 'Escape') cancelEditNombre(); }}
                      />
                      <button onClick={saveNombre} disabled={savingNombre} className="p-1.5 rounded-md text-green-600 hover:bg-green-50 disabled:opacity-50">
                        <Check className="w-4 h-4" />
                      </button>
                      <button onClick={cancelEditNombre} disabled={savingNombre} className="p-1.5 rounded-md text-gray-400 hover:bg-gray-50 disabled:opacity-50">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {nombreError && <p className="text-xs text-red-600">{nombreError}</p>}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-800">{user.nombre}</p>
                    <button onClick={startEditNombre} className="p-1 rounded-md text-gray-400 hover:text-[#3c98ff] hover:bg-blue-50">
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Email */}
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 mb-1">Email</p>
                {editingEmail ? (
                  <div className="space-y-2">
                    {emailStep === 'edit' ? (
                      <>
                        <div className="flex items-center gap-2">
                          <input
                            type="email"
                            value={emailDraft}
                            onChange={(e) => setEmailDraft(e.target.value)}
                            className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#3c98ff]"
                            disabled={savingEmail}
                            autoFocus
                            onKeyDown={(e) => { if (e.key === 'Enter') requestEmail(); if (e.key === 'Escape') cancelEditEmail(); }}
                          />
                          <button onClick={requestEmail} disabled={savingEmail} className="p-1.5 rounded-md text-green-600 hover:bg-green-50 disabled:opacity-50">
                            <Check className="w-4 h-4" />
                          </button>
                          <button onClick={cancelEditEmail} disabled={savingEmail} className="p-1.5 rounded-md text-gray-400 hover:bg-gray-50 disabled:opacity-50">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-xs text-gray-500">Se enviará un código de verificación al nuevo correo.</p>
                      </>
                    ) : (
                      <>
                        <p className="text-xs text-gray-600">
                          Se envió un código a <strong>{pendingNewEmail}</strong>. Ingrésalo para confirmar.
                        </p>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={verifyCode}
                            onChange={(e) => setVerifyCode(e.target.value)}
                            placeholder="Código de verificación"
                            className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#3c98ff]"
                            disabled={verifyingEmail}
                            autoFocus
                            onKeyDown={(e) => { if (e.key === 'Enter') confirmEmail(); if (e.key === 'Escape') cancelEditEmail(); }}
                          />
                          <button onClick={confirmEmail} disabled={verifyingEmail} className="p-1.5 rounded-md text-green-600 hover:bg-green-50 disabled:opacity-50">
                            <Check className="w-4 h-4" />
                          </button>
                          <button onClick={cancelEditEmail} disabled={verifyingEmail} className="p-1.5 rounded-md text-gray-400 hover:bg-gray-50 disabled:opacity-50">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </>
                    )}
                    {emailError && <p className="text-xs text-red-600">{emailError}</p>}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-800">{user.email}</p>
                    <button onClick={startEditEmail} className="p-1 rounded-md text-gray-400 hover:text-[#3c98ff] hover:bg-blue-50">
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Cargo */}
            <div className="flex items-start gap-3">
              <Briefcase className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 mb-1">Cargo</p>
                {editingCargo ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={cargoDraft}
                        onChange={(e) => setCargoDraft(e.target.value)}
                        placeholder="Ej: Gerente de Proyectos"
                        className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#3c98ff]"
                        disabled={savingCargo}
                        autoFocus
                        onKeyDown={(e) => { if (e.key === 'Enter') saveCargo(); if (e.key === 'Escape') cancelEditCargo(); }}
                      />
                      <button onClick={saveCargo} disabled={savingCargo} className="p-1.5 rounded-md text-green-600 hover:bg-green-50 disabled:opacity-50">
                        <Check className="w-4 h-4" />
                      </button>
                      <button onClick={cancelEditCargo} disabled={savingCargo} className="p-1.5 rounded-md text-gray-400 hover:bg-gray-50 disabled:opacity-50">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {cargoError && <p className="text-xs text-red-600">{cargoError}</p>}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-800">{user.cargo || <span className="text-gray-400 italic">Sin cargo definido</span>}</p>
                    <button onClick={startEditCargo} className="p-1 rounded-md text-gray-400 hover:text-[#3c98ff] hover:bg-blue-50">
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </Card>

      {/* Change password section */}
      <Card>
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-gray-400" />
            <h3 className="text-base font-semibold text-brand-dark">Cambiar Contraseña</h3>
          </div>

          {passwordSuccess && (
            <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              Contraseña actualizada exitosamente.
            </div>
          )}

          {passwordError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {passwordError}
            </div>
          )}

          <form onSubmit={handleChangePassword} className="space-y-4">
            <Input
              label="Contraseña actual"
              type="password"
              placeholder="••••••••"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              disabled={isSubmitting}
              required
            />
            <Input
              label="Nueva contraseña"
              type="password"
              placeholder="••••••••"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={isSubmitting}
              required
            />
            <Input
              label="Confirmar nueva contraseña"
              type="password"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isSubmitting}
              required
            />
            <Button type="submit" variant="primary" isLoading={isSubmitting} loadingText="Guardando...">
              Actualizar Contraseña
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
}
